import { FC, useState, useMemo, useEffect } from 'react';
import { Grid, Typography } from '@mui/material';
import { useGuardianState } from '../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useLocation, useNavigate } from 'react-router-dom';
import TabGroup from '../../../../components/tabGroup';
import { TabItem } from '../../../../components/components.interfaces';
import { pages } from '../../../../../utils/common';
import { AcademicsFiltersProvider } from '../../../academics/contexts/academicsFilters.context';
import AcademicsFilterBar from '../../../academics/components/AcademicsFilterBar.component';
import GuardianTimetableView from './subviews/timetable/guardianTimetable.view';
import GuardianCoursesClassesView from './subviews/coursesClasses/guardianCoursesClasses.view';
import GuardianLessonPlansView from './subviews/lessonPlans/guardianLessonPlans.view';
import GuardianAssessmentsView from './subviews/assessments/guardianAssessments.view';
import GuardianHomeworkView from './subviews/homework/guardianHomework.view';
import GuardianAttendanceView from './subviews/attendance/guardianAttendance.view';
import GuardianLibraryView from './subviews/library/guardianLibrary.view';
import GuardianMaterialsView from './subviews/materials/guardianMaterials.view';

const GuardianAcademicView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const academicTabs = useMemo<TabItem[]>(() => {
    const tabs: TabItem[] = [];
    const academicPages = pages.guardian?.subPages?.academics?.subPages;

    if (academicPages?.schedule) {
      tabs.push({
        label: translateText('guardian.academics.timetable.title'),
        tabPanel: <GuardianTimetableView />,
        onClickHandler: () => {
          navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${academicPages.schedule.path}`);
        },
      });
    }
    if (academicPages?.curriculum) {
      tabs.push({
        label: translateText('guardian.academics.coursesClasses.title'),
        tabPanel: <GuardianCoursesClassesView />,
        onClickHandler: () => {
          navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${academicPages.curriculum.path}`);
        },
      });
    }
    // Always add lesson plans tab
    tabs.push({
      label: translateText('guardian.academics.lessonPlans.title'),
      tabPanel: <GuardianLessonPlansView />,
      onClickHandler: () => {
        const lessonPlansPath = academicPages?.lessonPlans?.path || '/lesson-plans';
        navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${lessonPlansPath}`);
      },
    });
    if (academicPages?.materials) {
      tabs.push({
        label: translateText('guardian.academics.materials.title'),
        tabPanel: <GuardianMaterialsView />,
        onClickHandler: () => {
          const materialsPath = academicPages.materials.path;
          navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${materialsPath}`);
        },
      });
    }
    if (academicPages?.assessments) {
      tabs.push({
        label: translateText('guardian.academics.assessments.title'),
        tabPanel: <GuardianAssessmentsView />,
        onClickHandler: () => {
          navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${academicPages.assessments.path}`);
        },
      });
    }
    if (academicPages?.homework) {
      tabs.push({
        label: translateText('guardian.academics.homework.title'),
        tabPanel: <GuardianHomeworkView />,
        onClickHandler: () => {
          navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${academicPages.homework.path}`);
        },
      });
    }
    if (academicPages?.attendance) {
      tabs.push({
        label: translateText('guardian.academics.attendance.title'),
        tabPanel: <GuardianAttendanceView />,
        onClickHandler: () => {
          navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${academicPages.attendance.path}`);
        },
      });
    }
    if (academicPages?.library) {
      tabs.push({
        label: translateText('guardian.academics.library.title'),
        tabPanel: <GuardianLibraryView />,
        onClickHandler: () => {
          navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${academicPages.library.path}`);
        },
      });
    }
    return tabs;
  }, [translateText, navigate]);

  const currentRoute = useMemo(() => {
    const path = location.pathname;
    const academicPath = `${pages.guardian.path}${pages.guardian.subPages?.academics?.path}`;
    if (path.startsWith(academicPath)) {
      const subPath = path.replace(academicPath, '').replace(/^\//, '').replace(/\/$/, '');
      
      // Check specific routes first (more specific paths before general ones)
      if (subPath === 'library' || subPath.startsWith('library/')) {
        return 'library';
      }
      if (subPath === 'attendance' || subPath.startsWith('attendance/')) {
        return 'attendance';
      }
      if (subPath === 'homework' || subPath.startsWith('homework/')) {
        return 'homework';
      }
      if (subPath === 'assessments' || subPath.startsWith('assessments/')) {
        return 'assessments';
      }
      if (subPath === 'lesson-plans' || subPath.startsWith('lesson-plans/')) {
        return 'lessonPlans';
      }
      if (subPath === 'materials' || subPath.startsWith('materials/')) {
        return 'materials';
      }
      if (subPath === 'curriculum' || subPath.startsWith('curriculum/')) {
        return 'curriculum';
      }
      if (subPath === 'schedule' || subPath.startsWith('schedule/') || subPath === '' || subPath === '/') {
        return 'schedule';
      }
    }
    return 'schedule';
  }, [location.pathname]);

  useEffect(() => {
    const routeMap: Record<string, number> = {
      schedule: 0,
      curriculum: 1,
      lessonPlans: 2,
      materials: 3,
      assessments: 4,
      homework: 5,
      attendance: 6,
      library: 7,
    };
    const tabIndex = routeMap[currentRoute] ?? 0;
    if (tabIndex >= 0 && tabIndex < academicTabs.length) {
      setSelectedTab(tabIndex);
    } else if (academicTabs.length > 0) {
      const academicPages = pages.guardian?.subPages?.academics?.subPages;
      if (academicPages?.schedule) {
        navigate(`${pages.guardian.path}${pages.guardian.subPages?.academics?.path}${academicPages.schedule.path}`, { replace: true });
      }
    }
  }, [currentRoute, academicTabs, navigate]);

  const handleTabChange = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < academicTabs.length && academicTabs[tabIndex].onClickHandler) {
      academicTabs[tabIndex].onClickHandler?.(String(tabIndex));
    }
  };

  return (
    <AcademicsFiltersProvider>
      <Grid
        container
        spacing={3}
        sx={{ p: 3, height: '100%' }}
        data-testid="guardian-academic-container"
      >
        <Grid item xs={12}>
          <Typography variant="h5">
            {translateText('guardian.academics.title')}
          </Typography>
          {selectedChildId && (
            <Typography variant="body2" color="text.secondary">
              {translateText('guardian.academics.selectedChild')}: {selectedChildId}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sx={{ height: 'calc(100% - var(--main-title-height))', overflow: 'auto' }}>
          <TabGroup
            tabs={academicTabs}
            initiallySelected={selectedTab}
            onTabChange={handleTabChange}
            filterSection={<AcademicsFilterBar isGuardian />}
            dataTestIdPrefix="guardian-academic"
            limitHeight={true}
          />
        </Grid>
      </Grid>
    </AcademicsFiltersProvider>
  );
};

export default GuardianAcademicView;
